import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CATS } from "../../data/pusheen";
import { useSound } from "../../sound/SoundContext";
import { burst } from "../../lib/burst";
import { toast } from "sonner";

const SEQ = ["ArrowUp","ArrowUp","ArrowDown","ArrowDown","ArrowLeft","ArrowRight","ArrowLeft","ArrowRight","b","a"];

// Falling treats overlay (used by konami + confetti)
function useRain() {
  const [drops, setDrops] = useState([]);
  const rain = useCallback((imgs, count = 40) => {
    const batch = Array.from({ length: count }).map((_, i) => ({
      id: `${Date.now()}-${i}`,
      x: Math.random() * 100,
      delay: Math.random() * 0.8,
      dur: 2.4 + Math.random() * 2,
      size: 34 + Math.random() * 42,
      rot: (Math.random() - 0.5) * 120,
      src: imgs[Math.floor(Math.random() * imgs.length)],
    }));
    setDrops(batch);
    setTimeout(() => setDrops([]), 5200);
  }, []);
  const layer = (
    <div className="pointer-events-none fixed inset-0 z-[55] overflow-hidden" aria-hidden="true">
      <AnimatePresence>
        {drops.map((d) => (
          <motion.img
            key={d.id}
            src={d.src}
            initial={{ y: -120, opacity: 0, rotate: 0 }}
            animate={{ y: "110vh", opacity: [0, 1, 1, 0.8], rotate: d.rot }}
            transition={{ duration: d.dur, delay: d.delay, ease: "easeIn" }}
            style={{ left: `${d.x}%`, width: d.size, position: "absolute", top: 0 }}
            alt=""
          />
        ))}
      </AnimatePresence>
    </div>
  );
  return { rain, layer };
}

export default function EasterEggs() {
  const { play } = useSound();
  const { rain, layer } = useRain();
  const [showCat, setShowCat] = useState(false);
  const [peek, setPeek] = useState(false);

  // Konami code
  useEffect(() => {
    let idx = 0;
    const onKey = (e) => {
      const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      if (key === SEQ[idx]) {
        idx++;
        if (idx === SEQ.length) {
          idx = 0;
          play("yay");
          rain([CATS.cookie, CATS.donut, CATS.heart, CATS.wave, CATS.sleep], 48);
          toast.custom(() => (
            <div className="flex items-center gap-3 bg-pusheen-cream border-[3px] border-pusheen-brown rounded-2xl px-4 py-3 shadow-[4px_4px_0_#4A3728]">
              <img src={CATS.donut} alt="" className="w-10 h-10 animate-wiggle" />
              <div>
                <p className="font-display font-extrabold text-pusheen-brown leading-tight">Secret unlocked!</p>
                <p className="font-body text-sm text-pusheen-brownlight">It&apos;s raining treats. You are officially a Pusheen legend.</p>
              </div>
            </div>
          ), { duration: 3500 });
        }
      } else {
        idx = key === SEQ[0] ? 1 : 0;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [play, rain]);

  // Peeking cat from the edge after a while
  useEffect(() => {
    const t = setTimeout(() => setPeek(true), 6000);
    return () => clearTimeout(t);
  }, []);

  const clickPeek = () => {
    play("meow");
    burst(70, window.innerHeight - 80, { count: 14, kinds: ["heart", "star"] });
    setShowCat(true);
    setPeek(false);
    setTimeout(() => setShowCat(false), 2600);
  };

  return (
    <>
      {layer}

      {/* Peeking cookie-cat easter egg (bottom-left) */}
      <AnimatePresence>
        {peek && (
          <motion.button
            initial={{ x: -90, rotate: -10 }}
            animate={{ x: -34, rotate: 6 }}
            exit={{ x: -110 }}
            transition={{ type: "spring", stiffness: 120, damping: 12 }}
            whileHover={{ x: -10 }}
            onClick={clickPeek}
            data-testid="peek-cat"
            aria-label="A cat is peeking!"
            className="fixed bottom-4 left-0 z-40 focus:outline-none"
          >
            <img src={CATS.cookie} alt="" className="w-20 sm:w-24 drop-shadow-[4px_4px_0_rgba(74,55,40,0.15)]" draggable="false" />
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showCat && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="fixed bottom-6 left-6 z-50 bg-white border-[3px] border-pusheen-brown rounded-2xl px-4 py-2 shadow-[4px_4px_0_#4A3728]"
          >
            <p className="font-display font-bold text-pusheen-brown">nya~ you found me! 💕</p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
